import React from 'react';
import {
  Grid,
  Box,
  Typography,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Button from '../UIKit/Button';
import Card from '../UIKit/Card';
import { useTranslation } from 'react-i18next';

const BadgeSeriesList = ({
  badgeSeries,
  badges,
  handleOpenViewBadgeDialog,
  handleViewSeriesProgress,
  handleOpenSeriesRewardDialog,
  handleOpenSeriesDialog,
  setConfirmDeleteSeries,
  getRarityColor,
}) => {
  const { t } = useTranslation(['badges', 'common']);

  return (
    <Grid container spacing={3}>
      {badgeSeries.map((series) => (
        <Grid item xs={12} sm={6} md={4} key={series._id}>
          <Card
            sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                display: 'flex',
                gap: 1,
              }}
            >
              <Tooltip title={t('common:actions.edit')}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenSeriesDialog('edit', series)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common:actions.delete')}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setConfirmDeleteSeries({ open: true, series })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box display="flex" alignItems="center" mb={2}>
              <Avatar
                src={series.image}
                alt={series.name}
                sx={{ width: 60, height: 60, mr: 2 }}
              />
              <Typography variant="h6">
                {series.name}
                {!series.active && (
                  <Chip
                    label={t('badges:management.series.inactive')}
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
            </Box>

            {/* Описание */}
            <Typography variant="body2" color="text.secondary" mb={2}>
              {series.description}
            </Typography>

            {/* Значки в серии */}
            <Typography variant="subtitle2" gutterBottom>
              {t('badges:management.series.badgeCount')} (
              {series.badges?.length || 0}):
            </Typography>

            {/* Здесь добавляем вывод изображений значков, входящих в серию */}
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {series.badges?.length > 0 ? (
                series.badges.map((badge) => {
                  // Проверяем, является ли badge строкой (ID) или объектом
                  const badgeObj =
                    typeof badge === 'string' ||
                    (typeof badge === 'object' && !badge.image)
                      ? badges.find((b) => b._id === badge)
                      : badge;

                  if (!badgeObj) return null;

                  return (
                    <Tooltip
                      key={typeof badge === 'string' ? badge : badgeObj._id}
                      title={badgeObj.name}
                    >
                      <Avatar
                        src={badgeObj.image}
                        alt={badgeObj.name}
                        sx={{
                          width: 30,
                          height: 30,
                          border: `2px solid ${getRarityColor(
                            badgeObj.rarity
                          )}`,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleOpenViewBadgeDialog(badgeObj)}
                      />
                    </Tooltip>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('badges:management.series.noData')}
                </Typography>
              )}
            </Box>

            {/* Требования */}
            <Typography variant="body2" gutterBottom>
              {t('badges:management.series.requiredBadges')}:{' '}
              <strong>{series.requiredBadgesCount || 0}</strong>{' '}
              {t('common:stats.of')} {series.badges?.length || 0}{' '}
              {t('badges:badge_plural')}
            </Typography>

            {/* Бонусные награды */}
            <Typography variant="subtitle2" gutterBottom mt={2}>
              {t('badges:management.series.bonusRewards')}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {series.bonusRewards?.experience > 0 && (
                <Chip
                  icon={<SchoolIcon />}
                  label={`${series.bonusRewards.experience} ${t('common:experience')}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {series.bonusRewards?.specialBadge && (
                <Tooltip title={t('badges:forms.specialBadge')}>
                  <Avatar
                    src={
                      typeof series.bonusRewards.specialBadge === 'string'
                        ? badges.find(
                            (b) => b._id === series.bonusRewards.specialBadge
                          )?.image
                        : series.bonusRewards.specialBadge.image
                    }
                    alt={t('badges:forms.specialBadge')}
                    sx={{
                      width: 24,
                      height: 24,
                      border: '2px solid gold',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      const badgeObj =
                        typeof series.bonusRewards.specialBadge === 'string'
                          ? badges.find(
                              (b) => b._id === series.bonusRewards.specialBadge
                            )
                          : series.bonusRewards.specialBadge;

                      if (badgeObj) handleOpenViewBadgeDialog(badgeObj);
                    }}
                  />
                </Tooltip>
              )}
              {!series.bonusRewards?.experience &&
                !series.bonusRewards?.specialBadge && (
                  <Typography variant="body2" color="text.secondary">
                    {t('badges:management.series.noRewards')}
                  </Typography>
                )}
            </Box>

            <Box mt={2} display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => handleViewSeriesProgress(series._id)}
              >
                {t('badges:management.series.progress')}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<TrendingUpIcon />}
                onClick={() => handleOpenSeriesRewardDialog(series)}
              >
                {t('badges:management.series.reward')}
              </Button>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default BadgeSeriesList;
